import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@rydres/api-client';
import { BRAND, addDaysYmd, localParts } from '@rydres/shared';
import { Notice, Spinner } from '@rydres/ui';
import { riderNotMine, riderRides, type RiderRide } from '../lib/passenger.js';
import { Shell, useRiderGate } from './Passenger.js';
import { RideRow, dayOf } from './RideRow.js';
import { Bell } from './RiderUpdates.js';

const SECTION = 'px-1 text-xs font-bold uppercase tracking-wide text-muted';
const OPEN = new Set(['requested', 'quoted', 'confirmed', 'referred']);
const todayFor = (r: RiderRide) => localParts(new Date(), r.driver.timezone).ymd;

/** rydres.com/me: asking for a ride, and today's and tomorrow's rides. The rest is under Booking. */
export function HomePage() {
  const { p, gate } = useRiderGate(
    'Your rides',
    `Sign in with the mobile number you book with to see your rides, ask your ${BRAND.name} driver for a ride and message them.`,
  );
  const [rides, setRides] = useState<RiderRide[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const signedIn = Boolean(p.me);
  useEffect(() => {
    if (!signedIn) return;
    const load = () => riderRides().then((r) => setRides(r.rides), () => setRides((x) => x ?? []));
    void load();
    const timer = setInterval(() => document.visibilityState === 'visible' && void load(), 60_000);
    const onVisible = () => document.visibilityState === 'visible' && void load();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [signedIn]);
  if (gate) return gate;

  const me = p.me!;
  const now = Date.now();
  const first = me.details.name?.trim().split(/\s+/)[0];
  const all = rides ?? [];
  const onDay = (offset: number) =>
    all
      .filter((r) => r.status !== 'expired' && r.status !== 'handed_off' && dayOf(r) === addDaysYmd(todayFor(r), offset))
      .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
  const near = new Set([...onDay(0), ...onDay(1)].map((r) => r.id));
  const later = all.filter((r) => !near.has(r.id) && OPEN.has(r.status) && Date.parse(r.startsAt) > now).length;
  const notMine = async (r: RiderRide) => {
    setError(null);
    try {
      setRides((await riderNotMine(r.id)).rides);
      void p.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    }
  };
  const day = (title: string, offset: number, empty: string) => {
    const list = onDay(offset);
    return (
      <section className="space-y-2" aria-label={title}>
        <h2 className={SECTION}>{title}</h2>
        {rides === null ? <Spinner /> : list.length === 0 ? <p className="px-1 text-sm text-ink2">{empty}</p> : list.map((r) => <RideRow key={r.id} ride={r} timeOnly onNotMine={(x) => void notMine(x)} />)}
      </section>
    );
  };

  return (
    <Shell>
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-black">{first ? `Hi, ${first}` : 'Your rides'}</h1>
        <Bell />
      </div>
      {error && <Notice tone="danger">{error}</Notice>}
      <Link to="/me/request" className="flex min-h-12 items-center justify-center rounded-2xl bg-brand px-4 py-3 font-bold text-on-brand shadow-soft brand-press">
        Add Ride Service
      </Link>
      {day("Today's Rides", 0, 'No rides today.')}
      {day("Tomorrow's Rides", 1, 'No rides tomorrow.')}

      <Link to="/me/booking" className="block rounded-2xl px-4 py-3 text-center text-sm font-bold text-muted">
        Calendar and ride history{later > 0 ? ` · ${later} more ${later === 1 ? 'ride' : 'rides'} after tomorrow` : ''}
      </Link>
    </Shell>
  );
}
